<template>
  <b-container class="mt-2 px-2">
    <validation-observer ref="sellerForm">

      <!-- #region::Form -->
      <b-form @submit="$event.preventDefault()">
        <b-form-row>

          <!-- #region::Title and close button -->
          <b-col
            md="12"
            class="align-self-center"
          >
            <div class="d-flex justify-content-between">
              <h3 class="align-self-end">
                {{ id ? 'Editar vendedor' : 'Nuevo vendedor' }}
              </h3>
              <b-button
                variant="flat-secondary"
                class="btn-icon rounded-circle"
                @click="onClose"
              >
                <feather-icon icon="XIcon" />
              </b-button>
            </div>
          </b-col>
          <!-- #endregion::Title and close button -->

          <b-col md="12">
            <hr>
          </b-col>

          <!-- #region::Seller name input -->
          <b-col md="12">
            <TextInputWithValidation
              v-model="fullName"
              vid="fullName"
              rules="required|min:2"
              type="text"
              label="Nombre"
              name="nombre"
              maxlength="200"
              placeholder="Escribe el nombre del vendedor"
            />
          </b-col>
          <!-- #endregion::Seller name input -->

          <!-- #region::Enterprise input -->
          <b-col md="12">
            <TextInputWithValidation
              v-model="enterpriseName"
              vid="enterpriseName"
              rules="required|min:2"
              type="text"
              label="Empresa"
              name="empresa"
              maxlength="200"
              placeholder="Escribe el nombre de la empresa"
            />
          </b-col>
          <!-- #endregion::Enterprise input -->

          <!-- #region::Email input -->
          <b-col md="12">
            <TextInputWithValidation
              v-model="userEmail"
              vid="email"
              rules="required|email_prefix"
              type="text"
              label="Correo electrónico"
              name="correo electrónico"
              append="@optimumequipo.com"
              placeholder="Escribe el correo electrónico"
              :disabled="passwordChanged"
              :append-disabled="true"
            />
          </b-col>
          <!-- #endregion::Email input -->

          <!-- #region::Phone input -->
          <b-col md="12">
            <TextInputWithValidation
              v-model="phone"
              vid="phone"
              rules="required|digits:10"
              type="text"
              label="Teléfono"
              name="teléfono"
              placeholder="Escribe el número telefónico"
            />
          </b-col>
          <!-- #endregion::Phone input -->

          <!-- #region::Password input -->
          <b-col
            v-if="id"
            md="12"
          >
            <!-- password -->
            <b-form-group
              label="Contraseña"
              label-for="reset-password-new"
            >
              <validation-provider
                #default="{ errors }"
                name="contraseña"
                vid="Password"
                rules="password"
              >
                <b-input-group
                  class="input-group-merge"
                  :class="errors.length > 0 ? 'is-invalid':null"
                >
                  <b-form-input
                    id="reset-password-new"
                    v-model="password"
                    :type="password1FieldType"
                    :state="errors.length > 0 ? false:null"
                    class="form-control-merge"
                    name="reset-password-new"
                    placeholder="············"
                    :disabled="emailChanged"
                  />
                  <b-input-group-append is-text>
                    <feather-icon
                      class="cursor-pointer"
                      :icon="password1ToggleIcon"
                      @click="togglePassword1Visibility"
                    />
                  </b-input-group-append>
                </b-input-group>
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>
          <!-- #endregion::Password input -->

          <!-- #region::Seller save button -->
          <b-col md="6">
            <b-button
              variant="principal-btn"
              class="my-1 my-md-0 w-100 principal-btn"
              @click="onSaveSeller"
            >
              {{ id ? 'Guardar cambios' : 'Crear vendedor' }}
            </b-button>
          </b-col>
          <!-- #endregion::Seller save button -->

          <!-- #region::Cancel save button -->
          <b-col md="6">
            <b-button
              variant="delete-btn"
              class="my-lg-0 w-100 delete-btn"
              @click="onClose"
            >
              Cancelar
            </b-button>
          </b-col>
          <!-- #endregion::Cancel save button -->

        </b-form-row>
      </b-form>
      <!-- #endregion::Form -->

    </validation-observer>
  </b-container>
</template>

<script>
// #region imports
import { mapActions } from 'vuex'
import {
  required, min, email, digits, emailPrefix,
} from '@validations'
import {
  BContainer, BFormRow, BCol, BForm, BButton, BFormGroup, BInputGroup, BFormInput, BInputGroupAppend,
} from 'bootstrap-vue'

import { ValidationObserver, ValidationProvider, localize } from 'vee-validate'
import TextInputWithValidation from '@/components/forms/TextInputWithValidation.vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import es from 'vee-validate/dist/locale/es.json'

import getError from '@/helpers/ErrorsHandler'
// #endregion

export default {
  components: {
    BCol,
    BForm,
    BButton,
    BContainer,
    BFormRow,
    BFormGroup,
    BInputGroup,
    BFormInput,
    BInputGroupAppend,
    ValidationObserver,
    ValidationProvider,
    TextInputWithValidation,
    // eslint-disable-next-line vue/no-unused-components
    ToastificationContent,
  },
  data() {
    return {
      id: null,
      fullName: '',
      enterpriseName: '',
      userEmail: '',
      phone: '',
      password: '',
      seller: null,
      // * 05/10/2022 - TAG: Validations
      required,
      min,
      email,
      digits,
      emailPrefix,
      // * Toggle Password
      password1FieldType: 'password',
    }
  },
  computed: {
    password1ToggleIcon() {
      return this.password1FieldType === 'password' ? 'EyeIcon' : 'EyeOffIcon'
    },
    emailChanged() {
      if (!this.seller) {
        return false
      }
      return this.userEmail !== this.seller.email.substring(0, this.seller.email.indexOf('@'))
    },
    passwordChanged() {
      if (!this.seller) {
        return false
      }
      return this.password.length > 0
    },
  },
  async created() {
    localize('es', es)
  },
  methods: {
    ...mapActions({
      createSeller: 'sellers/createSeller',
      updateSeller: 'sellers/updateSeller',
    }),
    async onSaveSeller() {
      const success = await this.$refs.sellerForm.validate()
      if (success) {
        this.$swal({
          title: 'Espere por favor',
          allowOutsideClick: false,
        })

        this.$swal.showLoading()

        const formData = new FormData()

        if (this.id) {
          formData.append('id', this.id)

          if (this.passwordChanged) {
            formData.append('password', this.password)
          }
        }

        formData.append('name', this.fullName)
        formData.append('company', this.enterpriseName)
        formData.append('email', `${this.userEmail}@optimumequipo.com`)
        formData.append('phone', this.phone)

        // for (const value of formData.values()) {
        //   console.log(value);
        // }

        try {
          if (this.id) {
            await this.updateSeller(formData)
            this.showSwalFire('¡El vendedor ha sido actualizado correctamente!')
          } else {
            await this.createSeller(formData)
            this.showSwalFire('¡El vendedor ha sido registrado correctamente!')
          }
        } catch (error) {
          this.$swal.close()
          this.showToast('Error de validación', getError(error), 'danger')
        }
      }
    },
    loadExistingSeller(seller) {
      this.seller = seller
      this.id = seller.IdUser
      this.fullName = seller.Name
      this.enterpriseName = seller.Company
      this.userEmail = seller.email.substring(0, seller.email.indexOf('@'))
      this.phone = seller.Phone
    },
    showSwalFire(message) {
      this.$swal.fire({
        title: 'Guardado',
        text: message,
        icon: 'success',
        showCancelButton: false,
        confirmButtonText: 'Continuar',
        allowEscapeKey: false,
        allowOutsideClick: false,
      }).then(result => {
        if (result.isConfirmed) {
          this.onClose()
          this.$emit('on:reload')
        }
      })
    },
    showToast(title, text, variant) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title, text, variant, icon: 'BellIcon',
        },
      })
    },
    onClose() {
      this.id = null
      this.fullName = ''
      this.enterpriseName = ''
      this.userEmail = ''
      this.phone = ''

      this.$refs.sellerForm.reset()
      this.$emit('on:close')
    },
    togglePassword1Visibility() {
      this.password1FieldType = this.password1FieldType === 'password' ? 'text' : 'password'
    },
  },
}
</script>
